import React, { useEffect, useState, useRef } from 'react';
import { ErpPopover } from '@/components/custom/erp/popover';
import { BaseSearchTrigger } from './base-trigger';
import { SelectSearchProps } from './type';

/**
 * Select search input for data table columns.
 * Displays as an InputSearch, shows Popover with options on focus.
 *
 * @example
 * ```tsx
 * renderSearchInput: (params) => (
 *   <SelectSearch
 *     {...params}
 *     options={[
 *       { value: 'active', label: 'Active' },
 *       { value: 'inactive', label: 'Inactive' }
 *     ]}
 *   />
 * )
 * ```
 */
export const SelectSearch: React.FC<SelectSearchProps> = ({
  value,
  onChange,
  onBlur,
  options = [],
  renderOption,
  placeholder
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption?.value || '';
  const [inputValue, setInputValue] = useState(displayValue);

  useEffect(() => {
    if (!open) setInputValue(displayValue);
  }, [displayValue, open]);

  const filteredOptions = inputValue
    ? options.filter(opt => opt.value.toLowerCase().includes(inputValue.toLowerCase()))
    : options;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!open) setOpen(true);
  };

  const handleInputClick = () => {
    setOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      handleSelectOption(filteredOptions[0].value);
    }
  };

  const handleSelectOption = (optionValue: string) => {
    const selected = options.find(opt => opt.value === optionValue);
    onChange(optionValue);
    setInputValue(selected?.value || '');
    setOpen(false);
    onBlur();
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setInputValue(displayValue);
      onBlur();
    }
  };

  return (
    <ErpPopover
      open={open}
      onOpenChange={handleOpenChange}
      disableToggle={true}
      onInteractOutside={(e: any) => {
        const triggerDiv = (e.target as Element)?.closest?.('.base-search-trigger');
        if (triggerDiv && triggerRef.current && triggerDiv.contains(triggerRef.current)) {
          e.preventDefault();
        }
      }}
      trigger={
        <BaseSearchTrigger
          ref={triggerRef}
          value={inputValue}
          hasValue={!!value}
          isOpen={open}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
      }
      content={
        <div className='max-h-64 min-w-full overflow-y-auto'>
          {filteredOptions.length > 0 &&
            filteredOptions.map(opt => {
              const isSelected = value === opt.value;

              if (renderOption) {
                return <div key={opt.value}>{renderOption(opt, isSelected, handleSelectOption)}</div>;
              }

              return (
                <div
                  key={opt.value}
                  className='cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-100'
                  onClick={() => handleSelectOption(opt.value)}
                >
                  {opt.label}
                </div>
              );
            })}
        </div>
      }
      contentClassName='w-36 p-0'
    />
  );
};
